// supabaseClient.js — shared Supabase client + session mutex helpers.
//
// SCHEMA NOTE (2026-05-15 halfvec migration): embedding columns on
// rp_history, world_facts, and story_events are halfvec(768) (was vector).
// Their matching retrieval RPCs are match_linked_memory(uuid, halfvec,
// text[], text[], int) and match_character_voice(text, halfvec, int).
// Callers MUST pre-stringify embeddings as '[v1,v2,...]' before sending —
// PostgREST's implicit JSON-array → halfvec coercion path doesn't use the
// HNSW index and times out under load. See the JSDoc on
// semanticRelevantMemory (conversationLinks.js) and
// getCharacterDiscordContext (discordContext.js) for details.
//
// Admin-only tables not queried from client code: writer_accounts (writer
// attribution for rp_history), dropped_indexes_log (rollback log for the
// 42 indexes dropped during the perf sweep).
//
// The auth helpers below are unrelated to the schema notes above; they
// solve a Web Locks contention issue documented in their own block.

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://dxdwnpnavlohkwtxfwgq.supabase.co'
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[MFML] Missing Supabase credentials. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY env vars.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

// ─── GLOBAL SESSION MUTEX ────────────────────────────────────────────────────
// Prevents multiple concurrent calls to getSession()/refreshSession() from
// fighting over the browser's Web Locks API, which causes "lock was released
// because another request stole it" errors. When that happens, auth fails,
// RLS blocks queries, and DM messages appear in the sidebar but not in the
// conversation (because the preview_text was updated before the lock error,
// but the subsequent SELECT to load messages fails due to dead session).
//
// Usage: import { safeGetSession, safeRefreshSession } from './supabaseClient.js'
//        const { data: { session } } = await safeGetSession();

let _getSessionPromise = null;
// Cached session result + timestamp. Reads within CACHE_TTL_MS skip the
// navigator.lock acquisition entirely — the lock contention was producing
// "Lock broken by another request with the 'steal' option" warnings 10×/min
// in production and intermittently bricking auth-dependent flows (RLS
// queries returning empty because session was being stolen mid-request).
const SESSION_CACHE_TTL_MS = 30000;
let _sessionCache = null;
let _sessionCacheAt = 0;

export function invalidateSessionCache() {
  _sessionCache = null;
  _sessionCacheAt = 0;
}

// Re-cache when supabase reports an auth state change so we don't serve a
// stale session after sign-in/out/token-refresh.
supabase.auth.onAuthStateChange((_event, session) => {
  if (session) {
    _sessionCache = { data: { session }, error: null };
    _sessionCacheAt = Date.now();
  } else {
    invalidateSessionCache();
  }
});

export async function safeGetSession({ forceRefresh = false } = {}) {
  // Fast path: serve from cache if recent. This is what 90%+ of callers
  // actually need — they just want to read the access_token to attach a
  // Bearer header. They don't need the SDK to re-validate the session.
  if (!forceRefresh && _sessionCache && (Date.now() - _sessionCacheAt) < SESSION_CACHE_TTL_MS) {
    return _sessionCache;
  }
  // If another caller is already fetching the session, piggyback on that request
  if (_getSessionPromise) {
    try { return await _getSessionPromise; }
    catch { /* If the shared promise rejected, fall through to a fresh call */ }
  }
  _getSessionPromise = supabase.auth.getSession();
  try {
    const result = await _getSessionPromise;
    if (result?.data?.session) {
      _sessionCache = result;
      _sessionCacheAt = Date.now();
    }
    return result;
  } finally {
    _getSessionPromise = null;
  }
}

let _refreshPromise = null;

export async function safeRefreshSession() {
  // If another caller is already refreshing, piggyback on that request
  if (_refreshPromise) {
    try { return await _refreshPromise; }
    catch { /* If the shared promise rejected, fall through to a fresh call */ }
  }
  _refreshPromise = supabase.auth.refreshSession();
  try {
    const result = await _refreshPromise;
    // Refresh always invalidates + repopulates the cache.
    if (result?.data?.session) {
      _sessionCache = result;
      _sessionCacheAt = Date.now();
    } else {
      invalidateSessionCache();
    }
    return result;
  } finally {
    _refreshPromise = null;
  }
}
